import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';

/**
 * The backend hands external services deep links in the legacy app's format —
 * `/app/?action=<base64 JSON>` with `{id: 'openView', params: {view}}`. The
 * Stripe customer portal's "Return to PocketGuard" is one: it names
 * settings/billing, but nothing here understood the format, so the wildcard
 * route dropped the user on the dashboard instead (POC-8830).
 *
 * The view is only followed when it looks like an internal path; anything
 * else — absent, unparsable, or foreign — falls back to the dashboard, which
 * is what the wildcard did before.
 */
export const legacyAppLinkGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => legacyActionTarget(route) ?? inject(Router).parseUrl('/dashboard');

/**
 * POC-8830, second pass: QA still came back to the dashboard. Only
 * `/app/?action=…` reached the guard above. The root (`/?action=…`), a legacy
 * path under the prefix (`/app/dashboard/settings/e30?action=…`) and the
 * wildcard all redirect to /dashboard and keep the query — measured on
 * localhost, each of them landed on `/dashboard?action=…` with the action
 * unread. Legacy read it on its root route, which every deep link came
 * through; here that route is the dashboard, so it is read there. Without a
 * followable action the dashboard opens as it always did.
 */
export const legacyActionOnDashboardGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
) => legacyActionTarget(route) ?? true;

/** Where an `openView` action in the query points, if it points anywhere safe. */
function legacyActionTarget(route: ActivatedRouteSnapshot): UrlTree | null {
  const raw = route.queryParamMap.get('action');
  if (!raw) return null;

  try {
    const action: unknown = JSON.parse(atob(raw));
    const view =
      typeof action === 'object' && action !== null
        ? (action as {
            id?: string;
            params?: { view?: string; viewParams?: unknown };
          })
        : null;

    if (
      view?.id === 'openView' &&
      typeof view.params?.view === 'string' &&
      /^[\w-]+(\/[\w-]+)*$/.test(view.params.view)
    ) {
      // Insights 2.0 links carry a report configuration (POC-8665); the
      // insights route reads the same action from its own query parameter.
      if (view.params.view === 'insights' && view.params.viewParams) {
        return inject(Router).createUrlTree(['/insights'], {
          queryParams: { action: raw },
        });
      }
      return inject(Router).parseUrl('/' + view.params.view);
    }
  } catch {
    // not an action this app can follow
  }

  return null;
}
